import { useState } from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { buildP2pPercentChartData, formatP2pPrice, getP2pBuyPrice, getP2pOptionColor, getP2pOptionLogo, parseP2pPercent } from '@/lib/p2p'
import { cn } from '@/lib/utils'
import { P2P_PERCENT_DATE_RANGES } from '@/config/p2p'
import { useGetP2pMarketPercentsQuery } from '@/store/api/p2pApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectP2pOptionType, selectSelectedP2pOptionId, selectP2pOptionWithSide, setSelectedP2pOption } from '@/store/slices/p2pSlice'
import type { P2PMarket, P2PMarketOption, P2pPercentDateRange } from '@/types/p2p'
import type { ChartConfig } from '@/components/ui/chart'

interface P2pMarketOutcomesProps {
	market: P2PMarket
	selectedOptionId?: string | null
}

function P2pOutcomeBuyButton({ label, price, side, isActive, onClick }: { label: string; price: string; side: 'yes' | 'no'; isActive: boolean; onClick: () => void }) {
	const isYes = side === 'yes'

	return (
		<button
			type='button'
			onClick={onClick}
			className={cn(
				'relative shrink-0 overflow-hidden rounded-lg border px-3 py-2.5 text-sm font-medium tracking-[-0.21px] transition-colors',
				isYes
					? cn('border-info', isActive ? 'bg-info text-white ring-1 ring-info/60' : 'bg-background text-info hover:bg-info/10')
					: cn('border-warning', isActive ? 'bg-warning text-white ring-1 ring-warning/60' : 'bg-background text-warning hover:bg-warning/10'),
			)}
		>
			{!isActive ? (
				<span
					className={cn('pointer-events-none absolute top-1/2 h-8 w-8 -translate-y-1/2 rounded-full opacity-60 blur-xl', isYes ? 'right-2 bg-info' : 'left-2 bg-warning')}
					aria-hidden
				/>
			) : null}
			<span className='relative whitespace-nowrap'>
				Buy | {label} ${price}
			</span>
		</button>
	)
}

export function P2pMarketOutcomes({ market, selectedOptionId }: P2pMarketOutcomesProps) {
	const dispatch = useAppDispatch()
	const buySide = useAppSelector(selectP2pOptionType)
	const [dateRange, setDateRange] = useState<P2pPercentDateRange>(P2P_PERCENT_DATE_RANGES[3])
	const { data: percentsResponse, isFetching: isLoading } = useGetP2pMarketPercentsQuery({
		market_id: market.id,
		duration: dateRange.rangeMs,
	})
	const percents = percentsResponse?.data ?? []
	const chartData = buildP2pPercentChartData(percents, market.options)
	const firstTimestamp = chartData[0]?.timestamp ?? Date.now()
	const lastTimestamp = chartData[chartData.length - 1]?.timestamp ?? firstTimestamp
	const xDomain: [number, number] = firstTimestamp === lastTimestamp ? [firstTimestamp - 60 * 60 * 1000, lastTimestamp + 60 * 60 * 1000] : [firstTimestamp, lastTimestamp]
	const xTicks = Array.from({ length: 5 }, (_, index) => xDomain[0] + ((xDomain[1] - xDomain[0]) * index) / 4)

	const chartConfig = market.options.reduce<ChartConfig>((config, option, index) => {
		config[option.id] = {
			label: option.name,
			color: getP2pOptionColor(option, index),
		}
		return config
	}, {})

	const handleSelectOption = (optionId: string) => {
		dispatch(setSelectedP2pOption(optionId))
	}

	const handleBuyClick = (option: P2PMarketOption, side: 'yes' | 'no') => {
		dispatch(selectP2pOptionWithSide({ optionId: option.id, side }))
	}

	return (
		<div className='w-full overflow-hidden rounded-2xl border border-border bg-card shadow-[0_4px_12px_0_hsl(var(--background)/0.2)]'>
			<div className='p-4 sm:p-5'>
				{isLoading ? (
					<div className='flex h-[280px] items-center justify-center'>
						<BetsCycleLoader size={32} aria-label='Loading chart' />
					</div>
				) : !chartData.length ? (
					<div className='flex h-[280px] items-center justify-center'>
						<p className='text-sm text-muted-foreground'>No percent history available yet.</p>
					</div>
				) : (
					<>
						<div className='mb-3 flex items-center justify-end gap-1' role='tablist' aria-label='Chart date range'>
							{P2P_PERCENT_DATE_RANGES.map((range) => (
								<button
									key={range.label}
									type='button'
									role='tab'
									aria-selected={dateRange.label === range.label}
									onClick={() => setDateRange(range)}
									className={cn(
										'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
										dateRange.label === range.label ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
									)}
								>
									{range.label}
								</button>
							))}
						</div>
						<ChartContainer config={chartConfig} className='aspect-auto h-[280px] w-full'>
							<LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
								<CartesianGrid stroke='hsl(var(--border))' strokeOpacity={0.5} vertical={false} />
								<XAxis
									dataKey='timestamp'
									type='number'
									scale='time'
									domain={xDomain}
									ticks={xTicks}
									allowDataOverflow
									tick={{ fill: 'hsl(var(--subtle-foreground))', fontSize: 12 }}
									tickFormatter={(value: number) =>
										new Date(value).toLocaleString(
											'en-US',
											dateRange.label === '1D' ? { hour: 'numeric', minute: '2-digit', hour12: false } : { month: 'short', day: 'numeric' },
										)
									}
									axisLine={false}
									tickLine={false}
								/>
								<YAxis
									orientation='right'
									domain={[0, 'auto']}
									tick={{ fill: 'hsl(var(--subtle-foreground))', fontSize: 12 }}
									axisLine={false}
									tickLine={false}
									width={36}
								/>
								<ChartTooltip
									content={
										<ChartTooltipContent
											labelFormatter={(_value, payload) => {
												const timestamp = Number(payload?.[0]?.payload?.timestamp)

												return Number.isFinite(timestamp)
													? new Date(timestamp).toLocaleString('en-US', {
															year: 'numeric',
															month: 'short',
															day: 'numeric',
															hour: 'numeric',
															minute: '2-digit',
															hour12: false,
														})
													: ''
											}}
											formatter={(value) => `${Number(value).toFixed(2)}%`}
										/>
									}
								/>
								{market.options.map((option, index) => (
									<Line
										key={option.id}
										type='monotone'
										dataKey={option.id}
										name={option.name}
										stroke={getP2pOptionColor(option, index)}
										strokeWidth={selectedOptionId === option.id ? 2.5 : 2}
										strokeOpacity={selectedOptionId && selectedOptionId !== option.id ? 0.4 : 1}
										dot={false}
										activeDot={{ r: 4, strokeWidth: 0 }}
									/>
								))}
							</LineChart>
						</ChartContainer>

						<div className='mt-2 flex flex-wrap items-center justify-center gap-5'>
							{market.options.map((option, index) => {
								const isSelected = selectedOptionId === option.id

								return (
									<div
										key={option.id}
										className={cn(
											'flex items-center gap-2 text-sm font-medium tracking-[-0.21px]',
											isSelected ? 'text-foreground' : 'text-muted-foreground',
											selectedOptionId && !isSelected && 'opacity-60',
										)}
									>
										<span className='h-2 w-2 rounded-full' style={{ backgroundColor: getP2pOptionColor(option, index) }} />
										{option.name}
									</div>
								)
							})}
						</div>
					</>
				)}
			</div>

			<div className='divide-y divide-border border-t border-border'>
				{market.options.map((option) => {
					const isSelected = selectedOptionId === option.id
					const yesPrice = formatP2pPrice(String(getP2pBuyPrice(option, 'yes')))
					const noPrice = formatP2pPrice(String(getP2pBuyPrice(option, 'no')))

					return (
						<div
							key={option.id}
							className={cn(
								'grid grid-cols-1 items-center gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-6 sm:px-5',
								isSelected && 'bg-background/60',
							)}
						>
							<button type='button' onClick={() => handleSelectOption(option.id)} className='flex min-w-0 items-center gap-3 text-left'>
								<img src={getP2pOptionLogo(option, market)} alt='' className='h-8 w-8 shrink-0 rounded-full object-cover' />
								<span className='truncate text-base font-medium tracking-[-0.24px] text-foreground'>{option.name}</span>
							</button>

							<span className='text-base font-medium tracking-[-0.24px] text-foreground sm:text-right'>{parseP2pPercent(option.percent).toFixed(2)}%</span>

							<div className='flex items-center gap-2 sm:justify-end'>
								<P2pOutcomeBuyButton
									label='Yes'
									price={yesPrice}
									side='yes'
									isActive={isSelected && buySide === 'yes'}
									onClick={() => handleBuyClick(option, 'yes')}
								/>
								<P2pOutcomeBuyButton label='No' price={noPrice} side='no' isActive={isSelected && buySide === 'no'} onClick={() => handleBuyClick(option, 'no')} />
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}
